import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

function EidCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let stars = [];
        let lanterns = [];
        let fireflies = [];
        let moonPulse = 0;
        let moonPulseDir = 1;

        // ── Resize ──────────────────────────────────────────────────────────
        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();

        // ── Stars ────────────────────────────────────────────────────────────
        const createStar = () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height * 0.75,
            r: Math.random() * 1.6 + 0.3,
            alpha: Math.random() * 0.5 + 0.4,
            da: (Math.random() * 0.008 + 0.004) * (Math.random() > 0.5 ? 1 : -1),
            color: Math.random() > 0.85 ? '#FFF8DC' : '#FFFFFF',
        });

        const tickStar = (s) => {
            s.alpha += s.da;
            if (s.alpha > 1) { s.alpha = 1; s.da = -Math.abs(s.da); }
            if (s.alpha < 0.2) { s.alpha = 0.2; s.da = Math.abs(s.da); }
        };

        const drawStar = (s) => {
            ctx.save();
            ctx.globalAlpha = s.alpha;
            ctx.shadowColor = s.color;
            ctx.shadowBlur = s.r * 3;
            ctx.fillStyle = s.color;
            ctx.beginPath();
            ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        };

        // ── Lanterns ─────────────────────────────────────────────────────────
        const LANTERN_COLORS = [
            { body: '#F59E0B', glow: '#FBBF24' }, // amber
            { body: '#EF4444', glow: '#FCA5A5' }, // rose-red
            { body: '#EC4899', glow: '#F9A8D4' }, // pink
            { body: '#8B5CF6', glow: '#C4B5FD' }, // violet
            { body: '#F97316', glow: '#FDBA74' }, // orange
        ];

        const createLantern = (fromBottom = false) => {
            const col = LANTERN_COLORS[Math.floor(Math.random() * LANTERN_COLORS.length)];
            return {
                x: Math.random() * canvas.width,
                y: fromBottom ? canvas.height + 60 + Math.random() * 200 : Math.random() * canvas.height,
                size: Math.random() * 18 + 12,
                vy: Math.random() * 0.4 + 0.25,
                sway: Math.random() * 1.6 - 0.8,
                swayT: Math.random() * Math.PI * 2,
                swaySpeed: Math.random() * 0.015 + 0.008,
                glowPulse: Math.random() * Math.PI * 2,
                glowSpeed: Math.random() * 0.03 + 0.015,
                col,
            };
        };

        const tickLantern = (l) => {
            l.y -= l.vy;
            l.swayT += l.swaySpeed;
            l.x += Math.sin(l.swayT) * l.sway * 0.4;
            l.glowPulse += l.glowSpeed;
            if (l.y < -120) {
                l.y = canvas.height + 60;
                l.x = Math.random() * canvas.width;
            }
        };

        const drawLantern = (l) => {
            const glow = 0.55 + Math.sin(l.glowPulse) * 0.35;
            const s = l.size;
            ctx.save();

            // Outer glow
            const radGrad = ctx.createRadialGradient(l.x, l.y, 0, l.x, l.y, s * 2.8);
            radGrad.addColorStop(0, `${l.col.glow}${Math.floor(glow * 80).toString(16).padStart(2, '0')}`);
            radGrad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = radGrad;
            ctx.beginPath();
            ctx.arc(l.x, l.y, s * 2.8, 0, Math.PI * 2);
            ctx.fill();

            // Body (lantern ellipse)
            ctx.globalAlpha = 0.88;
            ctx.fillStyle = l.col.body;
            ctx.shadowColor = l.col.glow;
            ctx.shadowBlur = s * 1.5;
            ctx.beginPath();
            ctx.ellipse(l.x, l.y, s * 0.55, s * 0.82, 0, 0, Math.PI * 2);
            ctx.fill();

            // Top cap
            ctx.fillStyle = alpha(l.col.glow, 0.9);
            ctx.shadowBlur = 0;
            ctx.beginPath();
            ctx.ellipse(l.x, l.y - s * 0.82, s * 0.38, s * 0.18, 0, 0, Math.PI * 2);
            ctx.fill();

            // Bottom tassel glow
            ctx.globalAlpha = 0.6;
            ctx.strokeStyle = l.col.glow;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(l.x, l.y + s * 0.82);
            ctx.lineTo(l.x, l.y + s * 1.2);
            ctx.stroke();

            // Highlight shine on body
            const shineGrad = ctx.createLinearGradient(l.x - s * 0.3, l.y - s * 0.5, l.x + s * 0.1, l.y + s * 0.2);
            shineGrad.addColorStop(0, 'rgba(255,255,255,0.3)');
            shineGrad.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.globalAlpha = 1;
            ctx.fillStyle = shineGrad;
            ctx.beginPath();
            ctx.ellipse(l.x - s * 0.12, l.y - s * 0.15, s * 0.22, s * 0.45, -0.4, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        // ── Fireflies ─────────────────────────────────────────────────────────
        const createFirefly = () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            r: Math.random() * 1.5 + 0.5,
            vx: (Math.random() - 0.5) * 0.5,
            vy: (Math.random() - 0.5) * 0.5,
            alpha: Math.random(),
            da: (Math.random() * 0.02 + 0.005) * (Math.random() > 0.5 ? 1 : -1),
        });

        const tickFirefly = (f) => {
            f.x += f.vx;
            f.y += f.vy;
            f.alpha += f.da;
            if (f.alpha > 1) { f.alpha = 1; f.da = -Math.abs(f.da); }
            if (f.alpha < 0) { f.alpha = 0; f.da = Math.abs(f.da); }
            if (f.x < 0 || f.x > canvas.width) f.vx *= -1;
            if (f.y < 0 || f.y > canvas.height) f.vy *= -1;
        };

        const drawFirefly = (f) => {
            ctx.save();
            ctx.globalAlpha = f.alpha;
            ctx.shadowColor = '#FFD700';
            ctx.shadowBlur = 6;
            ctx.fillStyle = '#FEF3C7';
            ctx.beginPath();
            ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        };

        // ── Crescent Moon ─────────────────────────────────────────────────────
        const drawMoon = () => {
            moonPulse += 0.012 * moonPulseDir;
            if (moonPulse > 1) { moonPulse = 1; moonPulseDir = -1; }
            if (moonPulse < 0.4) { moonPulse = 0.4; moonPulseDir = 1; }

            const mx = canvas.width - 130;
            const my = 110;
            const mr = 55;

            // Outer atmospheric glow
            for (let i = 3; i >= 1; i -= 1) {
                const glowGrad = ctx.createRadialGradient(mx, my, mr * 0.6, mx, my, mr * (1.5 + i * 0.7));
                glowGrad.addColorStop(0, `rgba(255,215,0,${(moonPulse * 0.15) / i})`);
                glowGrad.addColorStop(1, 'rgba(255,200,0,0)');
                ctx.fillStyle = glowGrad;
                ctx.beginPath();
                ctx.arc(mx, my, mr * (1.5 + i * 0.7), 0, Math.PI * 2);
                ctx.fill();
            }

            // Moon body — golden gradient
            const moonGrad = ctx.createRadialGradient(mx - 10, my - 12, 4, mx, my, mr);
            moonGrad.addColorStop(0, '#FFFDE7');
            moonGrad.addColorStop(0.4, '#FFD700');
            moonGrad.addColorStop(1, '#B8860B');
            ctx.fillStyle = moonGrad;
            ctx.shadowColor = '#FFD700';
            ctx.shadowBlur = 25;
            ctx.beginPath();
            ctx.arc(mx, my, mr, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;

            // Shadow disk to create crescent shape (destination-out using composite)
            ctx.save();
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.beginPath();
            ctx.arc(mx + mr * 0.42, my - mr * 0.12, mr * 0.88, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            // Small star near crescent
            const sx = mx - 22;
            const sy = my - 42;
            ctx.save();
            ctx.globalAlpha = moonPulse;
            ctx.fillStyle = '#FFFDE7';
            ctx.shadowColor = '#FFD700';
            ctx.shadowBlur = 8;
            for (let i = 0; i < 5; i += 1) {
                const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
                const outerR = 7;
                const innerR = 3;
                const cos = Math.cos;
                const sin = Math.sin;
                if (i === 0) ctx.beginPath();
                const ox = sx + cos(angle) * outerR;
                const oy = sy + sin(angle) * outerR;
                const ix = sx + cos(angle + Math.PI / 5) * innerR;
                const iy = sy + sin(angle + Math.PI / 5) * innerR;
                if (i === 0) ctx.moveTo(ox, oy);
                else ctx.lineTo(ox, oy);
                ctx.lineTo(ix, iy);
            }
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        };

        // ── Initialize ────────────────────────────────────────────────────────
        const init = () => {
            stars = Array.from({ length: 130 }, createStar);
            lanterns = Array.from({ length: 10 }, () => createLantern(false));
            fireflies = Array.from({ length: 25 }, createFirefly);
        };
        init();

        // ── Animate ───────────────────────────────────────────────────────────
        const animate = () => {
            // Clear with deep background
            const bgGrad = ctx.createLinearGradient(0, 0, 0, canvas.height);
            bgGrad.addColorStop(0, '#0A0015');
            bgGrad.addColorStop(0.45, '#0D0830');
            bgGrad.addColorStop(1, '#1A0F40');
            ctx.fillStyle = bgGrad;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Subtle horizon shimmer
            const shimmer = ctx.createLinearGradient(0, canvas.height * 0.65, 0, canvas.height);
            shimmer.addColorStop(0, 'rgba(180,120,0,0)');
            shimmer.addColorStop(1, 'rgba(100,60,0,0.18)');
            ctx.fillStyle = shimmer;
            ctx.fillRect(0, canvas.height * 0.65, canvas.width, canvas.height * 0.35);

            stars.forEach(s => { tickStar(s); drawStar(s); });
            fireflies.forEach(f => { tickFirefly(f); drawFirefly(f); });

            drawMoon();

            lanterns.forEach(l => { tickLantern(l); drawLantern(l); });

            animationFrameId = requestAnimationFrame(animate);
        };
        animate();

        const handleResize = () => { resize(); init(); };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                pointerEvents: 'none',
            }}
        />
    );
}

// ── Mosque SVG Silhouette ───────────────────────────────────────────────────
function MosqueSilhouette() {
    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                pointerEvents: 'none',
                opacity: 0.22,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'flex-end',
                justifyContent: 'center',
            }}
        >
            <svg
                viewBox="0 0 1200 320"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: '100%', height: 'auto', display: 'block' }}
            >
                <defs>
                    <linearGradient id="mosqueGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#D4AF37" />
                        <stop offset="100%" stopColor="#8B6914" />
                    </linearGradient>
                </defs>
                {/* Central dome */}
                <ellipse cx="600" cy="200" rx="110" ry="90" fill="url(#mosqueGrad)" />
                <rect x="490" y="200" width="220" height="120" fill="url(#mosqueGrad)" />
                {/* Central minaret tip */}
                <polygon points="600,80 608,200 592,200" fill="url(#mosqueGrad)" />
                <ellipse cx="600" cy="82" rx="10" ry="5" fill="url(#mosqueGrad)" />
                {/* Left small dome */}
                <ellipse cx="400" cy="230" rx="70" ry="55" fill="url(#mosqueGrad)" />
                <rect x="330" y="230" width="140" height="90" fill="url(#mosqueGrad)" />
                {/* Left minaret */}
                <rect x="310" y="140" width="24" height="120" fill="url(#mosqueGrad)" />
                <polygon points="322,110 330,140 314,140" fill="url(#mosqueGrad)" />
                {/* Right small dome */}
                <ellipse cx="800" cy="230" rx="70" ry="55" fill="url(#mosqueGrad)" />
                <rect x="730" y="230" width="140" height="90" fill="url(#mosqueGrad)" />
                {/* Right minaret */}
                <rect x="866" y="140" width="24" height="120" fill="url(#mosqueGrad)" />
                <polygon points="878,110 886,140 870,140" fill="url(#mosqueGrad)" />
                {/* Ground */}
                <rect x="0" y="310" width="1200" height="10" fill="url(#mosqueGrad)" />
                {/* Arched windows */}
                <path d="M570,255 Q600,235 630,255 L630,320 L570,320 Z" fill="#0D0830" />
                <path d="M375,265 Q400,248 425,265 L425,320 L375,320 Z" fill="#0D0830" />
                <path d="M775,265 Q800,248 825,265 L825,320 L775,320 Z" fill="#0D0830" />
            </svg>
        </Box>
    );
}

// ── Main EidTheme ────────────────────────────────────────────────────────────
export default function EidTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' },
                px: { xs: 2, md: 12 },
                overflow: 'hidden',
                background: '#0A0015',
            }}
        >
            {/* Animated canvas */}
            <EidCanvas />

            {/* Mosque silhouette */}
            <MosqueSilhouette />

            {/* ── Login Card ───────────────────────────────────────────────── */}
            <Card
                sx={{
                    p: { xs: 3, sm: 4, md: 5 },
                    width: 1,
                    maxWidth: 460,
                    zIndex: 10,
                    position: 'relative',

                    // Clean solid background for readability
                    bgcolor: isDark
                        ? 'rgba(15, 10, 40, 0.95)'
                        : 'rgba(255, 255, 255, 0.97)',
                    backdropFilter: 'blur(24px)',
                    WebkitBackdropFilter: 'blur(24px)',

                    borderRadius: 3,
                    border: `1px solid ${alpha('#D4AF37', 0.4)}`,

                    // Layered shadow for depth
                    boxShadow: [
                        `0 0 0 1px ${alpha('#D4AF37', 0.15)}`,
                        `0 4px 24px ${alpha('#000', 0.4)}`,
                        `0 16px 64px ${alpha('#000', 0.35)}`,
                        `inset 0 1px 0 ${alpha('#fff', 0.1)}`,
                    ].join(', '),

                    // Animated gold top border glow
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '10%',
                        right: '10%',
                        height: '2px',
                        background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                        borderRadius: '0 0 4px 4px',
                        animation: 'goldShimmer 3s ease-in-out infinite',
                    },
                    '@keyframes goldShimmer': {
                        '0%, 100%': { opacity: 0.6, left: '10%', right: '10%' },
                        '50%': { opacity: 1, left: '5%', right: '5%' },
                    },

                    // Subtle corner ornament glow
                    '&:after': {
                        content: '""',
                        position: 'absolute',
                        inset: -1,
                        borderRadius: 'inherit',
                        background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, transparent 50%, rgba(212,175,55,0.08) 100%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                {/* Eid Mubarak badge */}
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: 10,
                            background: 'linear-gradient(90deg, #D4AF37, #FFD700, #D4AF37)',
                            backgroundSize: '200% 100%',
                            animation: 'badgeShimmer 2.5s ease infinite',
                            '@keyframes badgeShimmer': {
                                '0%': { backgroundPosition: '100% 0' },
                                '100%': { backgroundPosition: '-100% 0' },
                            },
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            letterSpacing: 2.5,
                            color: '#3A2600',
                            textTransform: 'uppercase',
                            boxShadow: `0 2px 12px ${alpha('#D4AF37', 0.4)}`,
                        }}
                    >
                        ☪️ &nbsp; Eid Mubarak &nbsp; ☪️
                    </Box>
                </Box>

                {/* Logo */}
                <Stack alignItems="center" sx={{ mb: 2 }}>
                    <Logo />
                </Stack>

                {/* Children — login form */}
                {cloneElement(children, { hideHelpLink: true })}
            </Card>

            {/* Help link */}
            <AuthHelpLink
                sx={{
                    position: 'fixed',
                    top: '50%',
                    right: 0,
                    transform: 'translateY(-50%) rotate(-90deg)',
                    transformOrigin: 'right center',
                    zIndex: 9999,
                    bgcolor: alpha('#D4AF37', 0.92),
                    p: 1,
                    px: 2.5,
                    borderRadius: '8px 8px 0 0',
                    boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                    backdropFilter: 'blur(8px)',
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    '& .MuiLink-root': {
                        color: '#3A2600',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                    },
                }}
            />

            {/* Crescent + star — top-left floating ornament */}
            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: 16, md: 36 },
                    left: { xs: 16, md: 56 },
                    zIndex: 3,
                    animation: 'floatY 6s ease-in-out infinite',
                    '@keyframes floatY': {
                        '0%, 100%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-12px)' },
                    },
                    filter: 'drop-shadow(0 0 18px rgba(212,175,55,0.8))',
                    fontSize: { xs: 40, md: 52 },
                    lineHeight: 1,
                    userSelect: 'none',
                }}
            >
                ☪️
            </Box>
        </Box>
    );
}

EidTheme.propTypes = {
    children: PropTypes.node,
};
